/**
 * Deterministic profile selection for login.
 *
 * One Supabase auth user can resolve to more than one `profiles` row — a
 * legitimate cross-tenant identity, or a legacy duplicate in the same tenant
 * (as with AstraZeneca's Justin Petersen). The login path must never throw on
 * that (no `.maybeSingle()`), and it must never pick the wrong row.
 *
 * The authority the backend uses (`current_enterprise_profile_id`) is: the
 * profile that bears an ACTIVE organisation assignment in the active tenant
 * wins; then a profile owned by the active tenant; only then recency. Recency
 * alone is unsafe — a stale duplicate touched more recently than the real one
 * would otherwise be chosen. This mirrors that ordering exactly.
 */

export interface ProfileCandidate {
  id: string;
  tenant_id?: string | null;
  updated_at?: string | null;
}

export interface ProfileSelectionContext {
  /** The tenant resolved from the current domain, if any. */
  domainTenantId?: string | null;
  /**
   * Ids of the candidate profiles that hold an ACTIVE organisation assignment
   * in `domainTenantId`. The caller supplies this (a targeted lookup) only when
   * there is more than one candidate, so the single-profile path stays cheap.
   */
  assignedProfileIds?: readonly string[];
}

function updatedAtMs(value: string | null | undefined): number {
  if (!value) return 0;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? 0 : ms;
}

function mostRecent<T extends ProfileCandidate>(rows: readonly T[]): T {
  return [...rows].sort((a, b) => updatedAtMs(b.updated_at) - updatedAtMs(a.updated_at))[0];
}

export function selectAuthProfile<T extends ProfileCandidate>(
  candidates: readonly T[] | null | undefined,
  context: ProfileSelectionContext = {},
): T | null {
  const list = (candidates || []).filter(Boolean);
  if (list.length <= 1) return list[0] ?? null;

  const { domainTenantId = null, assignedProfileIds = [] } = context;
  const assigned = new Set(assignedProfileIds);

  // 1. Authority: a profile bearing an active assignment in the active tenant.
  //    A more-recently-updated stale row must never outrank this.
  const assignmentBearing = list.filter((row) => assigned.has(row.id));
  if (assignmentBearing.length > 0) return mostRecent(assignmentBearing);

  // 2. Otherwise a profile owned by the active tenant.
  if (domainTenantId) {
    const owned = list.filter((row) => row.tenant_id === domainTenantId);
    if (owned.length > 0) return mostRecent(owned);
  }

  // 3. Last resort: most recently updated.
  return mostRecent(list);
}

/**
 * Look up the candidate profile ids that hold an ACTIVE assignment in the
 * active tenant. Implementations must THROW on any backend error — never
 * return an empty/partial list on failure — so the resolver can fail closed.
 */
export type AssignedProfileIdLookup = (
  profileIds: readonly string[],
  domainTenantId: string,
) => Promise<readonly string[]>;

/**
 * Resolve the single profile to log a session in, disambiguating duplicates via
 * the active assignment. FAIL CLOSED on two counts when there is more than one
 * candidate: (a) if the active tenant could not be resolved, reject — without
 * it the assignment-bearing profile cannot be identified and a recency guess
 * could select a stale duplicate; (b) if the assignment lookup errors, reject.
 * The single-profile login takes the fast path and needs neither the tenant nor
 * the lookup, so it never rejects on these grounds.
 */
export async function resolveAuthProfile<T extends ProfileCandidate>(
  candidates: readonly T[] | null | undefined,
  options: { domainTenantId?: string | null; lookupAssignedProfileIds: AssignedProfileIdLookup },
): Promise<T | null> {
  const list = (candidates || []).filter(Boolean);
  if (list.length <= 1) return list[0] ?? null;

  const domainTenantId = options.domainTenantId ?? null;
  if (!domainTenantId) {
    // More than one profile and no active tenant: refuse rather than guess.
    throw new Error(
      'Cannot disambiguate multiple profiles without a resolved active tenant',
    );
  }

  // A throw here propagates: the caller must treat it as a hard failure and
  // refuse the session rather than resolve to a possibly-wrong profile.
  const assignedProfileIds = await options.lookupAssignedProfileIds(
    list.map((row) => row.id),
    domainTenantId,
  );
  return selectAuthProfile(list, { domainTenantId, assignedProfileIds });
}
